import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { Button } from '@/components/common/UI/Button'

interface InterviewVideo {
  id: number
  video_type: 'introduction' | 'question' | 'answer'
  duration: number
  question?: string | null
  answer_to?: number | null
  expires_at?: string | null
  is_viewed: boolean
  created_at: string
}

const VideoPreview = ({ video }: { video: InterviewVideo }) => {
  const [src, setSrc] = useState('')
  useEffect(() => {
    let objectUrl = ''
    let cancelled = false
    apiClient.get<Blob>(`/v1/videos/${video.id}/content`, { responseType: 'blob' }).then((blob) => {
      if (!cancelled) {
        objectUrl = URL.createObjectURL(blob)
        setSrc(objectUrl)
      }
    }).catch(() => setSrc(''))
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl) }
  }, [video.id])
  return src ? <video className="mt-2 max-h-72 w-full rounded bg-black" controls preload="metadata" src={src} /> : <p className="mt-2 text-xs text-secondary-500">Загружаем защищённое видео…</p>
}

export default function VideoInterviewPanel({ applicationId, employer = false }: { applicationId: number; employer?: boolean }) {
  const queryClient = useQueryClient()
  const recorder = useRef<MediaRecorder | null>(null)
  const stream = useRef<MediaStream | null>(null)
  const startedAt = useRef(0)
  const timer = useRef<number | null>(null)
  const [seconds, setSeconds] = useState(0)
  const [recording, setRecording] = useState(false)
  const [questionText, setQuestionText] = useState('')
  const [error, setError] = useState('')
  const queryKey = ['applications', applicationId, 'videos']
  const { data: videos = [], isLoading } = useQuery({
    queryKey,
    queryFn: () => apiClient.get<InterviewVideo[]>(`/v1/applications/${applicationId}/videos`),
  })
  const upload = useMutation({
    mutationFn: async ({ file, type, answerTo, question }: { file: File; type: InterviewVideo['video_type']; answerTo?: number; question?: string }) => {
      const form = new FormData()
      const duration = Math.max(1, Math.min(60, Math.round((Date.now() - startedAt.current) / 1000)))
      form.append('file', file)
      form.append('duration', String(duration))
      form.append('video_type', type)
      if (answerTo) form.append('answer_to', String(answerTo))
      if (question) form.append('question', question)
      return apiClient.post(`/v1/applications/${applicationId}/videos`, form, { headers: { 'Content-Type': 'multipart/form-data' } })
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
    onError: () => setError('Не удалось отправить видео. Проверьте формат, размер до 100 МБ и срок ответа.'),
  })

  const startRecording = async (type: InterviewVideo['video_type'], answerTo?: number, question?: string) => {
    setError('')
    try {
      const media = await navigator.mediaDevices.getUserMedia({ video: true, audio: true })
      stream.current = media
      const mime = ['video/webm;codecs=vp9,opus', 'video/webm', 'video/mp4'].find((candidate) => MediaRecorder.isTypeSupported(candidate))
      const activeRecorder = new MediaRecorder(media, mime ? { mimeType: mime } : undefined)
      const chunks: BlobPart[] = []
      startedAt.current = Date.now()
      setSeconds(0)
      activeRecorder.ondataavailable = (event) => { if (event.data.size) chunks.push(event.data) }
      activeRecorder.onstop = () => {
        const elapsed = Math.max(1, Math.min(60, Math.round((Date.now() - startedAt.current) / 1000)))
        const fileType = activeRecorder.mimeType || 'video/webm'
        const extension = fileType.includes('mp4') ? 'mp4' : 'webm'
        const file = new File([new Blob(chunks, { type: fileType })], `interview-${Date.now()}.${extension}`, { type: fileType })
        media.getTracks().forEach((track) => track.stop())
        stream.current = null
        setRecording(false)
        setSeconds(elapsed)
        upload.mutate({ file, type, ...(answerTo ? { answerTo } : {}), ...(question ? { question } : {}) })
      }
      recorder.current = activeRecorder
      activeRecorder.start(500)
      setRecording(true)
      timer.current = window.setInterval(() => {
        const elapsed = Math.floor((Date.now() - startedAt.current) / 1000)
        setSeconds(elapsed)
        if (elapsed >= 60) activeRecorder.stop()
      }, 500)
    } catch {
      setError('Разрешите доступ к камере и микрофону в браузере, чтобы записать видео.')
    }
  }

  const stopRecording = () => {
    if (timer.current !== null) window.clearInterval(timer.current)
    timer.current = null
    if (recorder.current?.state === 'recording') recorder.current.stop()
  }

  useEffect(() => () => {
    if (timer.current !== null) window.clearInterval(timer.current)
    stream.current?.getTracks().forEach((track) => track.stop())
    if (recorder.current?.state === 'recording') recorder.current.stop()
  }, [])

  const answeredIds = new Set(videos.filter((video) => video.video_type === 'answer').map((video) => video.answer_to))
  const openQuestions = videos.filter((video) => video.video_type === 'question' && video.expires_at && new Date(video.expires_at).getTime() > Date.now() && !answeredIds.has(video.id))

  return (
    <section className="mt-4 border-t border-secondary-200 pt-4">
      <h4 className="font-medium text-sm">Видео-визитка и видео-вопросы</h4>
      {isLoading ? <p className="mt-2 text-xs text-secondary-500">Загружаем видео…</p> : <div className="mt-2 space-y-3">
        {videos.map((video) => <article key={video.id} className="rounded-lg bg-secondary-50 p-3">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <p className="text-sm font-medium">{video.video_type === 'introduction' ? 'Видео-визитка' : video.video_type === 'question' ? 'Вопрос работодателя' : 'Ответ кандидата'} · {video.duration} сек.</p>
              {video.question && <p className="mt-1 text-sm text-secondary-700">{video.question}</p>}
              {video.video_type === 'question' && video.expires_at && <p className="mt-1 text-xs text-secondary-500">Ответить до {new Date(video.expires_at).toLocaleString()}</p>}
            </div>
            {employer && video.video_type !== 'question' && <span className="text-xs text-secondary-500">{video.is_viewed ? 'Просмотрено' : 'Новое'}</span>}
          </div>
          <VideoPreview video={video} />
          {!employer && video.video_type === 'question' && openQuestions.some((item) => item.id === video.id) && <Button className="mt-2" size="sm" disabled={recording || upload.isPending} onClick={() => startRecording('answer', video.id)}>Записать ответ (до 60 сек.)</Button>}
        </article>)}
        {!videos.length && <p className="text-xs text-secondary-500">Видео пока нет.</p>}
      </div>}

      {employer ? <div className="mt-3 space-y-2">
        <textarea value={questionText} onChange={(event) => setQuestionText(event.target.value)} maxLength={2000} rows={2} placeholder="Текст вопроса кандидату" className="w-full rounded border border-secondary-300 px-3 py-2 text-sm" />
        <Button size="sm" disabled={recording || upload.isPending || questionText.trim().length < 3} onClick={() => startRecording('question', undefined, questionText.trim())}>{recording ? `Запись ${seconds}/60 сек.` : 'Записать видео-вопрос'}</Button>
      </div> : <Button className="mt-3" size="sm" disabled={recording || upload.isPending} onClick={() => startRecording('introduction')}>{recording ? `Запись ${seconds}/60 сек.` : 'Записать видео-визитку'}</Button>}
      {recording && <Button className="ml-2 mt-3" size="sm" variant="outline" onClick={stopRecording}>Остановить и отправить</Button>}
      {upload.isPending && <p className="mt-2 text-xs text-secondary-500">Отправляем видео…</p>}
      {error && <p className="mt-2 text-xs text-error">{error}</p>}
      <p className="mt-2 text-xs text-secondary-400">Видео хранится приватно, доступно только участникам отклика и ограничено 60 секундами / 100 МБ.</p>
    </section>
  )
}
